<script setup lang="ts">
import AITripDescription from '@apps/main/new-desktop/core/components/AITripDescription.vue';
import { PremadeTripType } from '@apps/main/new-desktop/core/models';
import {
  useBrowserStore,
  useItineraryStore,
  useMapStore,
  usePaxStore,
  usePremadeTripStore,
  usePriceCalendarStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';

import { AppPortableText } from '@libs/ui';

import {
  useTripOverviewModalsStore,
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
} from '../../stores';

import PremadeTripHeaderDesktop from './PremadeTripHeaderDesktop.vue';
import PremadeTripHeaderMobile from './PremadeTripHeaderMobile.vue';
import TripOverviewHeaderActions from './TripOverviewHeaderActions.vue';

const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const mapStore = useMapStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();

const premadeTripStore = usePremadeTripStore();
const browserStore = useBrowserStore();
const paxStore = usePaxStore();
const statsigEventsStore = useStatsigEventsStore();
const priceCalendarStore = usePriceCalendarStore();
const itineraryStore = useItineraryStore();

interface TripOverviewHeaderDesktopProps {
  printMode: boolean;
}

const props = defineProps<TripOverviewHeaderDesktopProps>();

interface TripOverviewHeaderDesktopEmits {
  (eventName: 'pax-click'): void;
  (eventName: 'edit-trip'): void;
  (eventName: 'map-click'): void;
  (eventName: 'home-airport-click'): void;
  (eventName: 'months-selector-click'): void;
  (eventName: 'open-premade-trip'): void;
  (eventName: 'navigate-to-car-trip'): void;
}

const emit = defineEmits<TripOverviewHeaderDesktopEmits>();

function openPaxSelectorModal(): void {
  emit('pax-click');
  tripOverviewPanelsStore.openConfigurationPanel(
    TabKey.Pax,
    'overview-header-actions'
  );
}

function openEditTripModal(): void {
  emit('edit-trip');
  tripOverviewPanelsStore.openConfigurationPanel(
    TabKey.Itinerary,
    'overview-header-actions'
  );
}

function onHomeAirportClick(): void {
  emit('home-airport-click');
  tripOverviewPanelsStore.openConfigurationPanel(
    TabKey.Airplane,
    'overview-header-actions'
  );
  statsigEventsStore.logTripOverviewFlyFromButtonClicked();
}

function openMonthsSelectorModal(): void {
  emit('months-selector-click');
  tripOverviewPanelsStore.openConfigurationPanel(
    TabKey.Calendar,
    'overview-header-actions'
  );
}

function openDynamicFullScreenMap(): void {
  emit('map-click');
  mapStore.openFullScreenDynamicTripMap();
}

function openPremadeTripDetailsModal(): void {
  emit('open-premade-trip');
  tripOverviewModalsStore.openPremadeTripDetailsModal();
}

function navigateToCarTrip(): void {
  emit('navigate-to-car-trip');
}
</script>

<template>
  <template v-if="premadeTripStore.showingPremadeTrip">
    <PremadeTripHeaderMobile
      v-if="browserStore.isSmallerThan1200px && premadeTripStore.premadeTrip"
      :trip-details="premadeTripStore.premadeTrip"
      @show-full-description="openPremadeTripDetailsModal"
    >
      <template #description>
        <AppPortableText
          v-if="
            premadeTripStore.premadeTrip.type ===
            PremadeTripType.PublishedInSanity
          "
          :portable-text="premadeTripStore.premadeTrip.description"
          type="premade-trip"
        />
        <AITripDescription
          v-if="premadeTripStore.premadeTrip.type === PremadeTripType.AI"
          :description="premadeTripStore.premadeTrip.description"
        />
      </template>
    </PremadeTripHeaderMobile>
    <PremadeTripHeaderDesktop
      v-if="!browserStore.isSmallerThan1200px && premadeTripStore.premadeTrip"
      :trip-details="premadeTripStore.premadeTrip"
      @show-full-description="openPremadeTripDetailsModal"
    >
      <template #description>
        <AppPortableText
          v-if="
            premadeTripStore.premadeTrip.type ===
            PremadeTripType.PublishedInSanity
          "
          :portable-text="premadeTripStore.premadeTrip.description"
          type="premade-trip"
          class="description"
        />
        <AITripDescription
          v-if="premadeTripStore.premadeTrip.type === PremadeTripType.AI"
          :description="premadeTripStore.premadeTrip.description"
        />
      </template>
    </PremadeTripHeaderDesktop>
  </template>

  <div
    v-if="
      !browserStore.isSmallerThan1200px && premadeTripStore.showingPremadeTrip
    "
    class="separator with-side-margin"
  />

  <div v-if="!props.printMode" class="actions">
    <TripOverviewHeaderActions
      :paxes-button-text="paxStore.getPaxesButtonDetailedText"
      :home-flight-data="tripOverviewPageStore.homeFlightData"
      :trip-start-date="itineraryStore.tripStartDate"
      :number-of-destinations="tripOverviewPageStore.numberOfDestinations"
      :number-of-nights="tripOverviewPageStore.numberOfNights"
      :price-calendar-button-text="priceCalendarStore.calendarTriggerText"
      :first-destination-name="tripOverviewPageStore.getFirstDestinationName()"
      :is-user-selected-itinerary="
        itineraryStore.userSelectedItinerary !== undefined
      "
      @pax-click="openPaxSelectorModal"
      @home-airport-click="onHomeAirportClick"
      @edit-trip="openEditTripModal()"
      @months-selector-click="openMonthsSelectorModal()"
      @map-click="openDynamicFullScreenMap"
      @navigate-to-car-trip="navigateToCarTrip"
    />
  </div>

  <div
    v-if="
      browserStore.isSmallerThan1200px && premadeTripStore.showingPremadeTrip
    "
    class="separator"
  />
</template>

<style scoped lang="scss">
.actions {
  padding: 0 var(--spacing-24) var(--spacing-8) var(--spacing-24);
  background-color: var(--color-white);
  @include for-less-or-equal-than-599-px {
    padding: 0 var(--spacing-16) var(--spacing-16) var(--spacing-24);
  }
}

.separator {
  border-bottom: 1px solid var(--color-neutral-30);
  margin-bottom: var(--spacing-8);

  &.with-side-margin {
    margin-left: var(--spacing-24);
    margin-right: var(--spacing-24);
  }
}
</style>
